<template>
  <slot></slot>
</template>

<script lang="ts" setup>
import { inject, ref, watch, onUnmounted } from "vue"
import type { Point, Size, Icon } from "@p-v/packages/Map/map"

interface ComponentProps {
  position: Point
  offset?: Size
  icon?: Icon
  title?: string
  draggable?: boolean
  zIndex?: number
  autoRotation?: boolean
  gpsType?: "GCJ02" | "WGS84" | "BD09"
  label?: {
    content: string
    direction: string
    offset: Size
  }
}

const props = withDefaults(defineProps<ComponentProps>(), {
  position: () => ({
    lng: 116.397428,
    lat: 39.90923,
  }),
  offset: () => ({ width: 0, height: 0 }),
  icon: undefined,
  title: "",
  draggable: false,
  zIndex: 100,
  autoRotation: false,
  gpsType: "GCJ02",
  label: undefined,
})

const amap = inject("amap") as any
const marker = ref<any>()

// 创建高德地图标记
const createMarker = () => {
  if (!amap.value) return

  // 处理不同坐标系
  let position = props.position
  if (props.gpsType === "BD09") {
    // 如果是百度坐标系，需要先转为高德坐标系
    try {
      const { BD09toGCJ02 } = require("@p-v/utils/amap")
      position = BD09toGCJ02(props.position.lng, props.position.lat)
    } catch (error) {
      console.error("坐标转换失败", error)
    }
  } else if (props.gpsType === "WGS84") {
    // 如果是WGS84坐标系，需要转为高德坐标系
    try {
      const { WGS84toGCJ02 } = require("@p-v/utils/amap")
      position = WGS84toGCJ02(props.position.lng, props.position.lat)
    } catch (error) {
      console.error("坐标转换失败", error)
    }
  }

  // 创建标记选项
  const markerOptions: any = {
    position: [position.lng, position.lat],
    title: props.title,
    draggable: props.draggable,
    zIndex: props.zIndex,
    autoRotation: props.autoRotation,
  }

  // 如果有偏移量
  if (props.offset) {
    markerOptions.offset = new window.AMap.Pixel(props.offset.width, props.offset.height)
  }

  // 如果有自定义图标
  if (props.icon) {
    markerOptions.icon = new window.AMap.Icon({
      image: props.icon.url,
      size: new window.AMap.Size(props.icon.size.width, props.icon.size.height),
      imageSize: props.icon.opts?.imageSize ? new window.AMap.Size(props.icon.opts.imageSize.width, props.icon.opts.imageSize.height) : undefined,
      imageOffset: props.icon.opts?.imageOffset
        ? new window.AMap.Pixel(props.icon.opts.imageOffset.width, props.icon.opts.imageOffset.height)
        : undefined,
    })
  }

  // 创建标记
  marker.value = new window.AMap.Marker(markerOptions)

  // 如果有文本标签
  if (props.label) {
    marker.value.setLabel({
      direction: props.label.direction || "right",
      offset: new window.AMap.Pixel(props.label.offset.width, props.label.offset.height),
      content: props.label.content,
    })
  }

  // 将标记添加到地图
  amap.value.add(marker.value)
}

// 监听地图对象是否已创建
watch(
  () => amap.value,
  val => {
    if (val) {
      createMarker()
    }
  },
  { immediate: true },
)

// 监听位置变化重新设置标记
watch(
  () => props.position,
  () => {
    if (marker.value && amap.value) {
      let position = props.position
      if (props.gpsType === "BD09") {
        try {
          const { BD09toGCJ02 } = require("@p-v/utils/amap")
          position = BD09toGCJ02(props.position.lng, props.position.lat)
        } catch (error) {
          console.error("坐标转换失败", error)
        }
      } else if (props.gpsType === "WGS84") {
        try {
          const { WGS84toGCJ02 } = require("@p-v/utils/amap")
          position = WGS84toGCJ02(props.position.lng, props.position.lat)
        } catch (error) {
          console.error("坐标转换失败", error)
        }
      }
      marker.value.setPosition([position.lng, position.lat])
    }
  },
  { deep: true },
)

// 组件销毁时移除标记
onUnmounted(() => {
  if (marker.value && amap.value) {
    amap.value.remove(marker.value)
    marker.value = null
  }
})

// 暴露标记对象
defineExpose({
  marker,
})
</script>
